/**
 * ConversasPanel.jsx — Rê Leve
 * Aba "Conversas Léve" da Janela do Operador.
 * Lê eventos do chatEventsLogger + liveSyncEngine em TEMPO REAL.
 *
 * Recebe via:
 *   1. BroadcastChannel (mesmo origin, instantâneo)
 *   2. storage event (cross-tab fallback)
 *   3. Polling 4s (garantia)
 *
 * Contadores separados: perguntas · respostas · pedidos · upsells · cross-sells
 * Lista de pedidos fechados com cliente, kit, valor e adicionais.
 */

import { useEffect, useMemo, useState } from 'react';
import { getChatEvents, getChatStats, clearChatEvents } from '../lib/chatEventsLogger';
import { subscribeAll, getSyncStatus } from '../lib/liveSyncEngine';
import { getGuardrails } from '../lib/knowledgeStoreEngine';
import { getPendingCount } from '../lib/pendingQuestionsEngine';
import { getAssistantName } from '../lib/assistantConfig';
import { getCredentials } from '../lib/credentialsEngine';

function fmtBRL(v) {
  return v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 });
}
function timeAgo(iso) {
  if (!iso) return '—';
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 5000) return 'agora';
  const s = Math.floor(ms / 1000);
  if (s < 60) return `há ${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `há ${m} min`;
  const h = Math.floor(m / 60);
  if (h < 24) return `há ${h}h`;
  return new Date(iso).toLocaleString('pt-BR');
}

export default function ConversasPanel() {
  const [tick, setTick] = useState(0);
  const [filter, setFilter] = useState('all'); // all | sales_relationship | faq
  const [lastEventAt, setLastEventAt] = useState(null);
  const guardrails = getGuardrails();
  const pendingQ = getPendingCount();
  const assistantName = getAssistantName();
  const syncStatus = getSyncStatus();

  // Force re-render a cada evento recebido
  useEffect(() => {
    const unsub = subscribeAll((evt) => {
      setLastEventAt(evt.ts);
      setTick(t => t + 1);
    });
    // Polling de segurança
    const timer = setInterval(() => setTick(t => t + 1), 4000);
    return () => { unsub(); clearInterval(timer); };
  }, []);

  const events = useMemo(() => getChatEvents({ limit: 200 }), [tick]);
  const stats = useMemo(() => getChatStats(), [tick]);

  const filtered = useMemo(() => {
    if (filter === 'all') return events;
    return events.filter(e => e.useCase === filter);
  }, [events, filter]);

  function handleClear() {
    if (confirm('Limpar todo o log de eventos? Isso afeta esta aba e quaisquer abas abertas no mesmo navegador.')) {
      clearChatEvents();
    }
  }

  return (
    <div className="rl-ops-panel">
      <div className="rl-ops-header">
        <div>
          <div className="rl-ops-eyebrow">
            Conversas com {assistantName} · sincronizado em tempo real
            <span className="rl-live-dot" title="Ao vivo (BroadcastChannel ativo)"></span>
          </div>
          <h1 className="rl-ops-title">O que tá acontecendo no chat agora</h1>
          <p className="rl-ops-sub">
            Cada mensagem do cliente e cada resposta da {assistantName} aparece aqui em segundos.
            Pedidos fechados, upsells aceitos e cross-sells também são contabilizados em separado.
            Última atualização: <strong>{timeAgo(lastEventAt || stats.lastEventAt)}</strong>.
          </p>
        </div>
        <div className="rl-ops-actions">
          <div className="rl-sync-pill">
            {syncStatus.broadcastChannel ? '🟢 BroadcastChannel ativo' : '🟡 Fallback storage event'}
            <small style={{ display: 'block', opacity: 0.7, fontSize: 9 }}>
              {stats.total} eventos no buffer
            </small>
          </div>
          <button className="rl-btn rl-btn--ghost rl-btn--small" onClick={handleClear}>🗑 Limpar</button>
          <button className="rl-ops-refresh" onClick={() => setTick(t => t + 1)}>↻</button>
        </div>
      </div>

      {/* Contadores separados — resolve o bug "conta 2" */}
      <div className="rl-counters-grid">
        <div className="rl-counter rl-counter--green">
          <div className="rl-counter-label">Perguntas</div>
          <div className="rl-counter-value">{stats.questions}</div>
          <div className="rl-counter-sub">do cliente</div>
        </div>
        <div className="rl-counter rl-counter--mostard">
          <div className="rl-counter-label">Respostas</div>
          <div className="rl-counter-value">{stats.answers}</div>
          <div className="rl-counter-sub">da {assistantName}</div>
        </div>
        <div className="rl-counter rl-counter--gold">
          <div className="rl-counter-label">Pedidos fechados</div>
          <div className="rl-counter-value">{stats.orders}</div>
          <div className="rl-counter-sub">{fmtBRL(stats.orderRevenue)} em vendas</div>
        </div>
        <div className="rl-counter rl-counter--coral">
          <div className="rl-counter-label">Upsell aceito</div>
          <div className="rl-counter-value">{stats.upsells}</div>
          <div className="rl-counter-sub">{fmtBRL(stats.upsellRevenue)} adicional</div>
        </div>
        <div className="rl-counter rl-counter--lilac">
          <div className="rl-counter-label">Cross-sell aceito</div>
          <div className="rl-counter-value">{stats.crossSells}</div>
          <div className="rl-counter-sub">{fmtBRL(stats.crossSellRevenue)} adicional</div>
        </div>
        <div className="rl-counter rl-counter--tomate">
          <div className="rl-counter-label">Pra aprovar</div>
          <div className="rl-counter-value">{pendingQ}</div>
          <div className="rl-counter-sub">{stats.pendingAnswered} já respondidas</div>
        </div>
      </div>

      {/* Filter */}
      <div className="rl-filter-group">
        <button className={'rl-chip ' + (filter === 'all' ? 'rl-chip--active' : '')}
          onClick={() => setFilter('all')}>Todas ({stats.questions + stats.answers})</button>
        <button className={'rl-chip ' + (filter === 'sales_relationship' ? 'rl-chip--active' : '')}
          onClick={() => setFilter('sales_relationship')}>🛒 Vendas ({stats.useCases.sales_relationship || 0})</button>
        <button className={'rl-chip ' + (filter === 'faq' ? 'rl-chip--active' : '')}
          onClick={() => setFilter('faq')}>❓ FAQ ({stats.useCases.faq || 0})</button>
      </div>

      <div className="rl-grid-2">
        {/* Stream de conversas */}
        <section className="rl-card">
          <div className="rl-card-head">
            <div>
              <div className="rl-card-eyebrow">Stream · {filtered.length} mensagens (mais recente em cima)</div>
              <div className="rl-card-title">💬 Chat em tempo real</div>
            </div>
          </div>
          <div className="rl-live-stream">
            {filtered.length === 0 && (
              <div className="rl-empty rl-empty--big">
                <div style={{ fontSize: 38, marginBottom: 8 }}>👀</div>
                Nenhuma conversa ainda. Abre o site do cliente em outra aba e digita algo — vai aparecer aqui em segundos.
              </div>
            )}
            {filtered.slice(0, 50).map((e, i) => (
              <div key={i} className={'rl-stream-msg rl-stream-msg--' + (e.role || 'bot')}>
                <div className="rl-stream-head">
                  <span className="rl-stream-time">{e.timestamp}</span>
                  <span className="rl-stream-role">
                    {e.role === 'user' ? '👤 Cliente' : `🌱 ${assistantName}`}
                  </span>
                  {e.useCase === 'faq' && <span className="rl-tag" style={{ background: '#E3F2FD', color: '#0D47A1' }}>FAQ</span>}
                  {e.useCase === 'sales_relationship' && <span className="rl-tag">vendas</span>}
                </div>
                <div className="rl-stream-text">{e.text}</div>
                <div className="rl-stream-meta">
                  {e.intent && <span className="rl-tag">intent: {e.intent}</span>}
                  {e.source && e.source !== 'local' && <span className="rl-tag" style={{ background: '#FFF4DD', color: '#C97C13' }}>{e.source}</span>}
                  {e.activeGuardrailIds && e.activeGuardrailIds.length > 0 && (
                    <span className="rl-tag" style={{ background: '#FCE7E5', color: '#B8332F' }}>
                      🛡 {e.activeGuardrailIds.join(', ')}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Lateral: pedidos + intents + sources */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          {/* Pedidos fechados — destaque */}
          <section className="rl-card rl-card--mostard">
            <div className="rl-card-head">
              <div>
                <div className="rl-card-eyebrow">Pedidos fechados via chatbot</div>
                <div className="rl-card-title">🛍️ {stats.orderList.length} pedidos · {fmtBRL(stats.orderRevenue)}</div>
              </div>
            </div>
            <div className="rl-list">
              {stats.orderList.length === 0 && (
                <div className="rl-empty">Nenhum pedido fechado ainda — quando o cliente clicar em "Confirmar este kit", aparece aqui.</div>
              )}
              {stats.orderList.slice(0, 8).map((o, i) => (
                <div key={i} className="rl-order-row">
                  <div className="rl-order-head">
                    <span className="rl-order-customer">👤 {o.customerName}</span>
                    <span className="rl-order-time">{timeAgo(o.at)}</span>
                  </div>
                  <div className="rl-order-kit">
                    <strong>{o.kitName}</strong> · {fmtBRL(o.kitPrice)}
                  </div>
                  {(o.upsells.length > 0 || o.crossSells.length > 0) && (
                    <div className="rl-order-adds">
                      {o.upsells.map((u, j) => (
                        <span key={'u'+j} className="rl-tag" style={{ background: '#FFE9C7', color: '#C97C13' }}>+ {u.name} · {fmtBRL(u.price)} <small>upsell</small></span>
                      ))}
                      {o.crossSells.map((c, j) => (
                        <span key={'c'+j} className="rl-tag" style={{ background: '#E8DEFF', color: '#5B2C9C' }}>↔ {c.name} · {fmtBRL(c.price)} <small>cross</small></span>
                      ))}
                    </div>
                  )}
                  <div className="rl-order-total">Total: {fmtBRL(o.total)}</div>
                </div>
              ))}
            </div>
          </section>

          {/* Intents */}
          <section className="rl-card">
            <div className="rl-card-head">
              <div>
                <div className="rl-card-eyebrow">Intenções detectadas</div>
                <div className="rl-card-title">Sobre o que falam</div>
              </div>
            </div>
            <div className="rl-list">
              {Object.entries(stats.intents).filter(([k]) => k).length === 0 && (
                <div className="rl-empty">
                  Nenhuma intenção identificada ainda. Quando a {assistantName} reconhecer pedidos típicos
                  (montar semana, calorias, promoções etc), eles aparecem aqui rankeados.
                </div>
              )}
              {Object.entries(stats.intents)
                .filter(([k]) => k && k !== 'fallback' && k !== 'unknown') // não são intents reais
                .sort((a, b) => b[1] - a[1])
                .slice(0, 6)
                .map(([intent, count]) => {
                  const max = Math.max(...Object.values(stats.intents).filter(v => v > 0));
                  return (
                    <div key={intent} className="rl-rank-row">
                      <div className="rl-rank-num"></div>
                      <div className="rl-rank-body">
                        <div className="rl-rank-name">{intent.replace(/_/g, ' ')}</div>
                        <div className="rl-rank-meta">{count}×</div>
                      </div>
                      <div className="rl-rank-bar">
                        <div className="rl-rank-bar-fill" style={{ width: ((count / max) * 100) + '%' }} />
                      </div>
                    </div>
                  );
                })}
            </div>
          </section>

          {/* Fontes de resposta — com label honesta + warning */}
          <SourceBreakdown stats={stats} />
        </div>
      </div>

      {/* Guardrails acionados */}
      <section className="rl-card" id="guardrails-card">
        <div className="rl-card-head">
          <div>
            <div className="rl-card-eyebrow">Guardrails acionados</div>
            <div className="rl-card-title">🛡 O que protegeu o cliente</div>
          </div>
        </div>
        <div className="rl-list">
          {Object.entries(stats.guardHits).length === 0 && (
            <div className="rl-empty">Nenhum guardrail acionado ainda.</div>
          )}
          {Object.entries(stats.guardHits)
            .sort((a, b) => b[1] - a[1])
            .map(([gid, count]) => {
              const g = guardrails.find(x => x.id === gid);
              if (!g) return null;
              return (
                <div key={gid} className={'rl-guard rl-guard--' + g.severity}>
                  <div className="rl-guard-head">
                    <span className="rl-guard-code">{g.id}</span>
                    <span className="rl-guard-name">{g.name}</span>
                    <span className={'rl-guard-sev rl-guard-sev--' + g.severity}>{g.severity}</span>
                    <span className="rl-tag" style={{ marginLeft: 'auto' }}>{count}×</span>
                  </div>
                  <div className="rl-guard-rule">{g.rule}</div>
                </div>
              );
            })}
        </div>
      </section>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SourceBreakdown — mostra LLM/Determinístico/Fallback com label HONESTA
// + warning se houver "LLM" sem OPENAI_API_KEY marcada como active
// ─────────────────────────────────────────────────────────────────────────────
function SourceBreakdown({ stats }) {
  const creds = getCredentials();
  const openaiActive = creds.openai?.status === 'active';
  const llmCount = stats.sources.llm || 0;
  const showWarning = llmCount > 0 && !openaiActive;

  // Labels honestas — não promete OpenAI a não ser que a chave esteja ativa
  const labels = {
    llm: openaiActive ? '🤖 LLM (OpenAI real)' : '🤖 LLM (flag do backend)',
    deterministic: '⚙️ Motor determinístico',
    fallback: '🔵 Fallback local',
    local: '📱 Motor local (sem backend)',
    faq_local: '❓ FAQ local',
    llm_backend: '🤖 LLM via backend',
  };

  return (
    <section className="rl-card">
      <div className="rl-card-head">
        <div>
          <div className="rl-card-eyebrow">Quem respondeu</div>
          <div className="rl-card-title">Fonte de cada resposta</div>
        </div>
      </div>

      {showWarning && (
        <div style={{
          background: '#FFF4DD',
          border: '1px solid #E89A2E',
          borderLeft: '4px solid #E89A2E',
          borderRadius: 10,
          padding: 12,
          marginBottom: 12,
          fontSize: 12,
          lineHeight: 1.55,
          color: '#5A3D0A',
        }}>
          <strong>⚠️ Atenção:</strong> backend marcou {llmCount} resposta{llmCount > 1 ? 's' : ''} como
          <strong> "LLM"</strong>, mas a chave <code style={{ background: 'rgba(0,0,0,0.06)', padding: '0 4px', borderRadius: 3 }}>OPENAI_API_KEY</code> ainda não está marcada como <strong>ativa</strong>
          na aba <em>Credenciais</em>. Pode ser que o backend esteja em modo demo retornando o flag.
          Confirme em <strong>Credenciais → OpenAI</strong>.
        </div>
      )}

      <div className="rl-list">
        {Object.entries(stats.sources).length === 0 && (
          <div className="rl-empty">Aguardando respostas.</div>
        )}
        {Object.entries(stats.sources)
          .sort((a, b) => b[1] - a[1])
          .map(([source, count]) => {
            const max = Math.max(...Object.values(stats.sources)) || 1;
            return (
              <div key={source} className="rl-rank-row">
                <div className="rl-rank-num"></div>
                <div className="rl-rank-body">
                  <div className="rl-rank-name">{labels[source] || source}</div>
                  <div className="rl-rank-meta">{count} respostas</div>
                </div>
                <div className="rl-rank-bar">
                  <div className="rl-rank-bar-fill" style={{ width: ((count / max) * 100) + '%' }} />
                </div>
              </div>
            );
          })}
      </div>
    </section>
  );
}
